// "Internal traffic": the owner's own devices, admin sessions and automated browsers. GA4's internal-traffic data
// filter keys on the event parameter `traffic_type` = "internal", so every GA event from such a browser carries it
// and the owner can exclude it in GA Admin. The flag is a single yes/no kept on that device only; nothing is sent
// to a server except the same yes/no on anonymous funnel counters, and it identifies no one.
//
// Turn it on for a device: open any page with ?fdp_internal=1 (off again with ?fdp_internal=0). Admin accounts are
// flagged automatically when they sign in on a device (see AuthAnalyticsTracker).

const KEY = "fdp-internal-v1";

function readFlag(): boolean {
  try { if (window.localStorage.getItem(KEY) === "1") return true; } catch { /* storage blocked */ }
  try { return window.sessionStorage.getItem(KEY) === "1"; } catch { return false; }
}

/** True when the browser reports it is automated (navigator.webdriver, or a headless user agent). */
export function isAutomatedBrowser(): boolean {
  try {
    const n = navigator as Navigator & { webdriver?: boolean };
    return n.webdriver === true || /HeadlessChrome|PhantomJS/i.test(n.userAgent ?? "");
  } catch { return false; }
}

/** Internal = flagged device/admin, or an automated browser (box and agent browsers). */
export function isInternalTraffic(): boolean {
  if (typeof window === "undefined") return false;
  return readFlag() || isAutomatedBrowser();
}

export function setInternalTraffic(on: boolean): void {
  try { on ? window.localStorage.setItem(KEY, "1") : window.localStorage.removeItem(KEY); } catch { /* ignore */ }
  try { on ? window.sessionStorage.setItem(KEY, "1") : window.sessionStorage.removeItem(KEY); } catch { /* ignore */ }
  try { window.gtag?.("set", { traffic_type: on ? "internal" : undefined }); } catch { /* ignore */ }
}

/** Applies ?fdp_internal=1|0 from a URL search string. Returns true when it changed the flag. */
export function applyInternalParam(search: string): boolean {
  const v = new URLSearchParams(search).get("fdp_internal");
  if (v !== "1" && v !== "0") return false;
  setInternalTraffic(v === "1");
  return true;
}
